import { useCallback, useRef, useState } from "react";

interface Props {
  tsrName: string | null;
  docName: string | null;
  parseError: string | null;
  hashProgress: number | null;
  onFiles: (files: File[]) => void;
  onLoadExample: () => void;
  onReset: () => void;
}

export function DropZone({
  tsrName,
  docName,
  parseError,
  hashProgress,
  onFiles,
  onLoadExample,
  onReset,
}: Props) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      onFiles(Array.from(e.dataTransfer.files));
    },
    [onFiles]
  );

  return (
    <section
      className={`dropzone${dragging ? " dragging" : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      aria-label="Drop timestamp response and original file"
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files) onFiles(Array.from(e.target.files));
          e.target.value = "";
        }}
      />
      {!tsrName ? (
        <div className="dropzone-empty">
          <p className="dropzone-title">
            Drop a <code className="mono">.tsr</code> file here
          </p>
          <p className="dropzone-sub">
            A <code className="mono">.tsr</code> is a signed receipt from a
            timestamp authority: proof that a specific hash existed at a
            specific time. Add the original file too and we'll prove it
            matches. Files are parsed locally — nothing is uploaded.
          </p>
          <div className="dropzone-actions">
            <button type="button" onClick={() => inputRef.current?.click()}>
              Choose files
            </button>
            <button type="button" className="ghost" onClick={onLoadExample}>
              Load example
            </button>
          </div>
        </div>
      ) : (
        <div className="dropzone-loaded">
          <div className="file-chips">
            <span className="chip chip-tsr" title="Timestamp response">
              ⏱ {tsrName}
            </span>
            {docName ? (
              <span className="chip chip-doc" title="Original document">
                📄 {docName}
                {hashProgress !== null && (
                  <progress value={hashProgress} max={1} />
                )}
              </span>
            ) : (
              <button
                type="button"
                className="chip chip-add"
                onClick={() => inputRef.current?.click()}
              >
                + add the original file to check its digest
              </button>
            )}
          </div>
          <button type="button" className="ghost" onClick={onReset}>
            Clear
          </button>
        </div>
      )}
      {parseError && (
        <p className="parse-error" role="alert">
          {parseError}
        </p>
      )}
    </section>
  );
}
